import React, { ElementType, useEffect, useRef } from "react";
import { createSafeContentNode } from "./createSafeContentNode";
import { Content, ContentNode, parseHtml } from "./parseHtml";
import { store } from "@/store";
import { VideoTracker, videoEventManager } from "@/lib/tour/video-tracker";
import { ShowContentStep } from "@/lib/tour/steps/v2/showContent";

// This component is can render user configured and potentially unsafe html content.
// The content is sanitized and only allowed tags and attributes are rendered.
export function DynamicContent<E extends ElementType = ElementType, P = {}>({
  content,
  videoTracker,
  is,
  ...rest
}: { content?: string; videoTracker?: VideoTracker; is?: E } & React.HTMLAttributes<HTMLDivElement> & P) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Clean up previous video listeners
    videoEventManager.detachVideoListeners(el);

    el.innerHTML = "";

    if (content) {
      const ast = parseHtml(content, (url) => store.assetManager.getAsset(url).url);
      const contentNode = createSafeContentNode(ast);
      if (contentNode) {
        el.appendChild(contentNode);
      }

      // Add data-video-id attributes to video elements if we have a ShowContentStep tracker
      if (videoTracker && videoTracker instanceof ShowContentStep) {
        const videoElements = el.querySelectorAll("video");
        const videoBlocks = videoTracker.videoBlocks;

        // Try to match video elements with video blocks by src attribute
        videoElements.forEach((videoElement, index) => {
          const videoSrc = videoElement.getAttribute("src");
          if (videoSrc) {
            // Find matching video block by src
            const matchingBlock = videoBlocks.find(
              (block) =>
                block.src === videoSrc ||
                // Handle URL encoding differences
                decodeURIComponent(block.src) === videoSrc ||
                block.src === decodeURIComponent(videoSrc)
            );

            if (matchingBlock) {
              videoElement.setAttribute("data-video-id", matchingBlock.id);
            } else {
              // Fallback: use video block by index if src doesn't match
              if (index < videoBlocks.length) {
                videoElement.setAttribute("data-video-id", videoBlocks[index].id);
              }
            }
          }

          // Autoplay the video
          const video = videoElement as HTMLVideoElement;
          video.muted = true; // Ensure muted for autoplay to work in most browsers
          video.playsInline = true; // For mobile browsers
          video.controls = false; // Hide video controls

          video.play().catch((error) => {
            // Silently handle autoplay failures
          });
        });
      }

      // Set up video tracking if a tracker is provided
      if (videoTracker) {
        videoEventManager.setTracker(videoTracker);
        videoEventManager.attachVideoListeners(el);
      }
    }

    // Cleanup on unmount
    return () => {
      if (el) {
        videoEventManager.detachVideoListeners(el);
      }
    };
  }, [content, videoTracker]);

  return React.createElement(is ?? "div", { ref: containerRef, "data-pyne-dynamic-content": "", ...rest });
}
